import { PhysioLayout } from "@/components/layout/physio-layout";
import { Stethoscope, CheckCircle } from "lucide-react";

const ESP_AREAS = [
  {
    title: "Scope of Practice",
    content: [
      "First-contact MSK physiotherapy assessment (first-contact practitioner / FCP)",
      "Rheumatology-specific extended assessment: inflammatory arthritis, connective tissue disease, fibromyalgia",
      "Imaging referral (X-ray, MRI, ultrasound) — where governance frameworks are in place",
      "Blood test ordering for monitoring (requires appropriate training and PGD/PSD)",
      "Injection therapy: corticosteroid, hyaluronic acid, and soft tissue injections",
    ],
  },
  {
    title: "Governance & Legal Framework",
    content: [
      "ESP roles must operate within a clear scope of practice document agreed with medical/clinical director",
      "Injection therapy requires Trust-approved competency sign-off and indemnity cover",
      "Drug administration: Patient Group Directions (PGDs) or Prescribing (Independent or Supplementary) required for injection agents",
      "HCPC fitness to practise: ensure scope remains within your personal competence",
      "Regular audit of extended scope practice activity is essential for governance",
    ],
  },
  {
    title: "Rheumatology ESP Competencies",
    content: [
      "Systematic musculoskeletal examination including peripheral and axial joints",
      "Recognition of inflammatory vs mechanical vs systemic presentations",
      "Interpretation of inflammatory markers (CRP, ESR, anti-CCP, RF) in clinical context",
      "Disease activity scoring: DAS28, BASDAI, ASDAS",
      "Early inflammatory arthritis pathway navigation and appropriate onward referral",
    ],
  },
  {
    title: "Injection Therapy",
    content: [
      "Intra-articular corticosteroid injections (knee, shoulder, wrist, small joints)",
      "Soft tissue injections: subacromial, trochanteric bursa, carpal tunnel, plantar fascia",
      "Ultrasound-guided injection — requires additional training and competency sign-off",
      "Post-injection advice, monitoring, and documentation standards",
      "Consent and adverse event management",
    ],
  },
];

export default function EspRoles() {
  return (
    <PhysioLayout title="Extended Scope Practice (ESP) Roles">
      <div className="space-y-6">
        <div className="bg-gradient-to-br from-blue-800 to-blue-950 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <Stethoscope className="h-8 w-8 text-blue-300" />
            <div>
              <h2 className="text-xl font-bold">Extended Scope Practice in Rheumatology</h2>
              <p className="text-blue-300 text-sm">ESP / FCP Roles · Injection Therapy · Advanced Assessment</p>
            </div>
          </div>
          <p className="text-blue-200 text-sm leading-relaxed">
            Extended Scope Practice (ESP) physiotherapists in rheumatology operate at the boundary of traditional physiotherapy and medicine, undertaking advanced assessment, investigation, and intervention under agreed governance frameworks.
          </p>
        </div>

        <div className="space-y-4">
          {ESP_AREAS.map((area) => (
            <div key={area.title} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
              <h3 className="font-bold text-slate-900 mb-3">{area.title}</h3>
              <ul className="space-y-2">
                {area.content.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
          <h3 className="font-bold text-slate-900 mb-3">Career Pathway to ESP Role</h3>
          <div className="space-y-2">
            {[
              { step: "1", label: "Band 7 physiotherapist with specialist MSK / rheumatology experience (3+ years)" },
              { step: "2", label: "Complete injection therapy training (e.g. ACPOM course, university module)" },
              { step: "3", label: "Agree scope of practice and governance framework with clinical director" },
              { step: "4", label: "Obtain Trust competency sign-off for each extended scope activity" },
              { step: "5", label: "Maintain a log of all ESP activity for audit and HCPC compliance" },
              { step: "6", label: "Regular supervision and peer review of ESP practice" },
            ].map((item) => (
              <div key={item.step} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center font-bold shrink-0 mt-0.5">{item.step}</div>
                <p className="text-sm text-slate-700">{item.label}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 leading-relaxed">
          ESP roles must be established within a clear governance framework. Always ensure your scope of practice is agreed with your line manager and medical director before undertaking any extended scope activity. Refer to the CSP guidance on extended scope practice (csp.org.uk).
        </p>
      </div>
    </PhysioLayout>
  );
}
